import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "../utils/auth";
import { handleAuthError } from "../utils/errorHandler";
import SocialAuthButtons from "./SocialAuthButtons";

const Login = () => {
  const navigate = useNavigate();
  const [loginMethod, setLoginMethod] = useState("username");
  const [formData, setFormData] = useState({ email: "", username: "", password: "" });
  const [emailUsernames, setEmailUsernames] = useState([]);
  const [selectedUsername, setSelectedUsername] = useState("");
  const [requiresUsernameSelection, setRequiresUsernameSelection] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "email" ? { password: "" } : {})
    }));
    if (e.target.name === "email") {
      setRequiresUsernameSelection(false);
      setEmailUsernames([]);
      setSelectedUsername("");
    }
    setError(""); // Clear error when user types
  };

  const resetEmailSelectionFlow = () => {
    setRequiresUsernameSelection(false);
    setEmailUsernames([]);
    setSelectedUsername("");
    setFormData((prev) => ({ ...prev, email: "", password: "" }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    console.log("Login form submitted");

    try {
      let payload;
      if (loginMethod === "username") {
        payload = { username: formData.username.trim(), password: formData.password };
      } else if (!requiresUsernameSelection) {
        payload = { email: formData.email.trim() };
      } else {
        payload = {
          email: formData.email.trim(),
          username: selectedUsername,
          password: formData.password
        };
      }

      console.log("Calling login function with:", {
        loginMethod,
        email: payload.email,
        username: payload.username,
        password: payload.password ? "******" : undefined
      });
      const userData = await login(payload);

      if (userData?.requiresUsernameSelection) {
        const usernames = Array.isArray(userData.usernames) ? userData.usernames : [];
        setEmailUsernames(usernames);
        setSelectedUsername(usernames[0]?.username || "");
        setRequiresUsernameSelection(usernames.length > 0);
        setError("");
        setLoading(false);
        return;
      }

      console.log("Login successful, userData:", JSON.stringify(userData, null, 2));
      
      // Verify localStorage has been set
      console.log("localStorage token:", localStorage.getItem('token'));
      console.log("localStorage token length:", localStorage.getItem('token') ? localStorage.getItem('token').length : 0);
      console.log("localStorage userId:", localStorage.getItem('userId'));
      console.log("localStorage isAuthenticated:", localStorage.getItem('isAuthenticated'));
      
      // Use direct window location navigation instead of React Router
      console.log("Preparing to redirect to chat page");
      
      // Add a short delay to ensure localStorage has been updated
      setTimeout(() => {
        console.log("Redirecting to chat page now");
        window.location.href = "/chat";
      }, 100);
    } catch (err) {
      console.error("Login error in component:", err);
      
      // Add more detailed error logging
      if (err.response) {
        console.error("Error response status:", err.response.status);
        console.error("Error response data:", JSON.stringify(err.response.data, null, 2));
        
        // Check if there's a specific message for 403 errors
        if (err.response.status === 403) {
          const errorMsg = err.response.data?.message || "Access denied";
          console.error("Setting error message to:", errorMsg);
          setError(errorMsg);
          setLoading(false);
          return;
        }
      }
      
      const errorMessage = handleAuthError(err);
      console.error("Final error message:", errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSocialAuthError = (errorMessage) => {
    console.log('🔍 Social auth error:', errorMessage);
    setError(errorMessage);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg border border-gray-100">
        <div className="text-center">
          <div className="flex justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="mt-4 text-3xl font-bold text-gray-900">
            Welcome back
          </h2>
          <p className="mt-2 text-gray-600 text-sm">
            Sign in to continue to your account
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => { setLoginMethod("username"); setError(""); }}
              className={`px-3 py-2 text-sm rounded-lg border ${
                loginMethod === "username"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300"
              }`}
            >
              Username Login
            </button>
            <button
              type="button"
              onClick={() => { setLoginMethod("email"); setError(""); }}
              className={`px-3 py-2 text-sm rounded-lg border ${
                loginMethod === "email"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300"
              }`}
            >
              Email Login
            </button>
          </div>

          <div className="rounded-md space-y-4">
            {loginMethod === "username" ? (
              <>
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="Your username"
                    value={formData.username}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="Your password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="off"
                      readOnly={emailUsernames.length > 0}
                      className={`appearance-none rounded-lg relative block w-full px-3 py-2 border placeholder-gray-500 focus:outline-none focus:z-10 sm:text-sm ${
                        emailUsernames.length > 0
                          ? "border-green-400 bg-green-50 text-gray-700 cursor-default focus:ring-0"
                          : "border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"
                      }`}
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  {emailUsernames.length > 0 && (
                    <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Email confirmed
                    </p>
                  )}
                </div>

                {emailUsernames.length > 0 && (
                  <>
                    <div className="text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
                      Found {emailUsernames.length} username{emailUsernames.length === 1 ? "" : "s"} for this email. Select one and enter its password.
                    </div>
                    <div>
                      <label htmlFor="selectedUsername" className="block text-sm font-medium text-gray-700 mb-1">Select Username</label>
                      <select
                        id="selectedUsername"
                        value={selectedUsername}
                        onChange={(e) => setSelectedUsername(e.target.value)}
                        required
                        className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                      >
                        {emailUsernames.map((u) => (
                          <option key={u.username} value={u.username}>
                            {u.username}{u.fullname ? ` — ${u.fullname}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="email-login-password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                      <input
                        id="email-login-password"
                        name="password"
                        type="password"
                        required
                        autoComplete="new-password"
                        className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                        placeholder="Enter password for selected username"
                        value={formData.password}
                        onChange={handleChange}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={resetEmailSelectionFlow}
                      className="text-sm text-blue-600 hover:text-blue-500 text-left"
                    >
                      ← Use a different email
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${
                loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200`}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                loginMethod === "email" && !requiresUsernameSelection
                  ? "Find usernames"
                  : "Sign in"
              )}
            </button>
          </div>
        </form>

        {/* Social Authentication Buttons */}
        <SocialAuthButtons 
          mode="login"
          onError={handleSocialAuthError}
          className="mt-6"
        />

          <div className="flex flex-col space-y-4 text-center text-sm">
            <p className="text-gray-600">
              New here?{' '}
              <a href="/signup" className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200">
                Create an account
              </a>
            </p>

            <p>
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
              >
                Forgot your password?
              </button>
            </p>

            <p>
              <a href="/admin-login" className="font-medium text-red-600 hover:text-red-500 transition-colors duration-200">
                Sign in as Admin
              </a>
            </p>

            {/* JD Publisher Login Notice */}
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-xs">
                <strong>JD Publisher Users:</strong> You can login from any URL - no restrictions!
              </p>
            </div>
          </div>
      </div>
    </div>
  );
};

export default Login;
